import { SportParser } from "../parser";
import { sportMapperFactory, liveCountMapper, preLiveCountMapper, todayCountMapper, SportsMapper, personalizedSportMapperFactory } from "../mapper";
import { SportsRetriever, FavoritesRetriever, SportPushesRetriever, LiveStreamingRetriever, LiveStreamingBaseRetriever, TodayLiveStreamingRetriever, SportsBaseRetriever, TodaySportsRetriever } from "../retriever";
import { liveSportBFFQueryFactory, liveSportApiQueryFactory, preLiveSportApiQueryFactory, preLiveSportBFFQueryFactoryConstructor, todaySportApiQueryFactory, todaySportBFFQueryFactory, liveLiveStreamingQueryFactory, upcomingLiveStreamingQueryFactory, todayLiveStreamingQueryFactory, personalizedSportBFFQueryFactory } from "../queries";
import { SportDataGateway, GlobalConfig } from "sb-bff-integration";
import { internalMessageBus } from "sb-internal-msg-bus";
import { Cookies } from "sb-helpers";
import { MessageBus, Clock, MessageSender, State, queryType, TimeZoneObservable, UserInfoObservable } from "../../../common";
import { liveStreamingMode, defaultItemForSportMode } from "../sportConfig";
import { PersonalizedRetriever, PersonalizedBaseRetriever } from "../retriever/Personalized";
import { PersonalizedSportsMapper } from "../mapper/PersonalizedSportsMapper";
export const getTimeZoneObservable = () => new TimeZoneObservable(getMessageBus());
export const getUserInfoObservable = () => new UserInfoObservable(getMessageBus());
export const sportsRetrieversFactory = (cfg, dict, timeZoneObservable, userInfoObservable, bffGateway) => {
    const sportsRetriever = getSportsRetriever(cfg, timeZoneObservable, userInfoObservable, dict, bffGateway);
    const retrievers = [sportsRetriever];
    if (cfg.enableFavorites) {
        retrievers.push(new FavoritesRetriever(getMessageBus(), cfg.isFavoritesFirst, cfg.blockId, dict, Cookies));
    }
    if (cfg.enableLiveStreaming) {
        retrievers.push(getLiveStreamingRetriever(cfg, dict, timeZoneObservable, bffGateway));
    }
    return retrievers;
};
const getSportsRetriever = (cfg, timeZoneObservable, userInfoObservable, dict, bffGateway) => {
    switch (cfg.query) {
        case queryType.live:
            return getLiveSportsRetriever(cfg, bffGateway);
        case queryType.preLive:
            return getPreLiveSportsRetriever(cfg, bffGateway);
        case queryType.today:
            return getTodaySportRetriever(cfg, timeZoneObservable, bffGateway);
        case queryType.personalized:
            return getPersonalizedSportRetriever(cfg, userInfoObservable, dict, bffGateway);
    }
};
const getLiveStreamingRetriever = (cfg, dict, timeZoneObservable, bffGateway) => {
    const baseRetriever = new LiveStreamingBaseRetriever(bffGateway, dict, cfg.liveStreamingUpdatePeriod, cfg.liveStreamingMode);
    switch (cfg.query) {
        case queryType.live:
            return new LiveStreamingRetriever(baseRetriever, liveLiveStreamingQueryFactory);
        case queryType.preLive:
            return new LiveStreamingRetriever(baseRetriever, () => upcomingLiveStreamingQueryFactory(new Date()));
        case queryType.today:
            return new TodayLiveStreamingRetriever(baseRetriever, timeZoneObservable, new Clock(Date), todayLiveStreamingQueryFactory);
        default:
            return new LiveStreamingRetriever(baseRetriever, () => upcomingLiveStreamingQueryFactory(new Date()));
    }
};
const getLiveSportsRetriever = (cfg, bffGateway) => {
    const bffQueryFactory = liveSportBFFQueryFactory;
    const sportApiQueryFactory = liveSportApiQueryFactory(GlobalConfig.apiConfigs.locale);
    const countMapper = liveCountMapper;
    const sportMapper = sportMapperFactory(countMapper, cfg.sportOrder);
    const sportsMapper = new SportsMapper(sportMapper);
    const sportPushesRetriever = new SportPushesRetriever(sportsMapper, countMapper, new SportDataGateway());
    const baseRetriever = new SportsBaseRetriever(bffGateway, sportsMapper, sportPushesRetriever, cfg.sportRetrieverUpdatePeriod);
    return new SportsRetriever(baseRetriever, bffQueryFactory, sportApiQueryFactory);
};
const getMessageBus = () => new MessageBus(internalMessageBus);
const getPreLiveSportsRetriever = (cfg, bffGateway) => {
    const bffQueryFactory = preLiveSportBFFQueryFactoryConstructor(Date);
    const sportApiQueryFactory = preLiveSportApiQueryFactory(GlobalConfig.apiConfigs.locale, Date);
    const countMapper = preLiveCountMapper;
    const sportMapper = sportMapperFactory(countMapper, cfg.sportOrder);
    const sportsMapper = new SportsMapper(sportMapper);
    const sportPushesRetriever = new SportPushesRetriever(sportsMapper, countMapper, new SportDataGateway());
    const baseRetriever = new SportsBaseRetriever(bffGateway, sportsMapper, sportPushesRetriever, cfg.sportRetrieverUpdatePeriod);
    return new SportsRetriever(baseRetriever, bffQueryFactory, sportApiQueryFactory);
};
const getTodaySportRetriever = (cfg, timeZoneObservable, bffGateway) => {
    const countMapper = todayCountMapper;
    const sportMapper = sportMapperFactory(countMapper, cfg.sportOrder);
    const sportsMapper = new SportsMapper(sportMapper);
    const sportPushesRetriever = new SportPushesRetriever(sportsMapper, countMapper, new SportDataGateway());
    const baseRetriever = new SportsBaseRetriever(bffGateway, sportsMapper, sportPushesRetriever, cfg.sportRetrieverUpdatePeriod);
    const sportApiQueryFactory = todaySportApiQueryFactory(GlobalConfig.apiConfigs.locale);
    return new TodaySportsRetriever(baseRetriever, timeZoneObservable, new Clock(Date), todaySportBFFQueryFactory, sportApiQueryFactory);
};
const getPersonalizedSportRetriever = (cfg, userInfoObservable, dict, bffGateway) => {
    const personalizationCfg = Object.assign({}, cfg.personalization);
    const personalizedSportMapper = personalizedSportMapperFactory(cfg.sportOrder);
    const personalizedSportsMapper = new PersonalizedSportsMapper(personalizedSportMapper);
    const baseRetriever = new PersonalizedBaseRetriever(bffGateway, dict, cfg.sportRetrieverUpdatePeriod, personalizedSportsMapper, personalizationCfg.enableForYouTab, personalizationCfg.maxPersonalizedSports, personalizationCfg.maxPersonalizedEventsPerSport, personalizationCfg.regionCode);
    return new PersonalizedRetriever(baseRetriever, personalizedSportBFFQueryFactory, userInfoObservable);
};
const isFavoritesShouldBeSelected = (cfg) => {
    return cfg.isFavoritesFirst && cfg.enableFavorites && !isLiveStreamingShouldBeSelected(cfg);
};
const isLiveStreamingShouldBeSelected = (cfg) => {
    return cfg.enableLiveStreaming && cfg.liveStreamingMode === liveStreamingMode.firstSelected;
};
const isForYouTabShouldBeSelected = (cfg) => {
    return cfg.query === queryType.personalized && cfg.personalization.enableForYouTab;
};
const getSelectedSportByConfig = (cfg) => {
    if (isFavoritesShouldBeSelected(cfg)) {
        return defaultItemForSportMode.favorites;
    }
    if (isLiveStreamingShouldBeSelected(cfg)) {
        return defaultItemForSportMode.liveStreaming;
    }
    if (isForYouTabShouldBeSelected(cfg)) {
        return defaultItemForSportMode.forYou;
    }
    return defaultItemForSportMode.firstSport;
};
export const sportsParserFactory = (cfg, retrievers) => {
    return new SportParser(retrievers, getMessageBus(), cfg.blockId);
};
export const sportsStateFactory = (cfg, domId) => {
    const messageSender = new MessageSender(getMessageBus(), cfg.blockId);
    return new State(messageSender, cfg.blockId, domId, getSelectedSportByConfig(cfg));
};
//# sourceMappingURL=factories.js.map